import React, { useState, useEffect, useRef, useMemo } from "react";
import "../css/checkoutMsg.scss";
import { useNavigate, useLocation, Link } from "react-router-dom";
import ReactFlagsSelect from "react-flags-select";
import PhoneInput, {
  formatPhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { BASE_URL, LIST_URL } from "../constans/ApiEndpointConstants";
import { sendMessage } from "../apis/SendMessageAPI";
import { getUrl } from "../apis/GetPkAPI";
import { PixelWithEvent } from "./common/PixelWithEvent ";
import * as FacebookAPI from "../apis/FacebookAPI";
import Cookies from "js-cookie";
import * as GetPixelAPI from "../apis/GetPixelAPI";
import { generateDonationUrl } from "../utils/urlUtils";
import { donateDeepColor } from "../constans/ColorConstants";

// 日本都道府县列表
const JAPAN_PREFECTURES = [
  { value: "北海道", label: "北海道" },
  { value: "青森県", label: "青森県" },
  { value: "岩手県", label: "岩手県" },
  { value: "宮城県", label: "宮城県" },
  { value: "秋田県", label: "秋田県" },
  { value: "山形県", label: "山形県" },
  { value: "福島県", label: "福島県" },
  { value: "茨城県", label: "茨城県" },
  { value: "栃木県", label: "栃木県" },
  { value: "群馬県", label: "群馬県" },
  { value: "埼玉県", label: "埼玉県" },
  { value: "千葉県", label: "千葉県" },
  { value: "東京都", label: "東京都" },
  { value: "神奈川県", label: "神奈川県" },
  { value: "新潟県", label: "新潟県" },
  { value: "富山県", label: "富山県" },
  { value: "石川県", label: "石川県" },
  { value: "福井県", label: "福井県" },
  { value: "山梨県", label: "山梨県" },
  { value: "長野県", label: "長野県" },
  { value: "岐阜県", label: "岐阜県" },
  { value: "静岡県", label: "静岡県" },
  { value: "愛知県", label: "愛知県" },
  { value: "三重県", label: "三重県" },
  { value: "滋賀県", label: "滋賀県" },
  { value: "京都府", label: "京都府" },
  { value: "大阪府", label: "大阪府" },
  { value: "兵庫県", label: "兵庫県" },
  { value: "奈良県", label: "奈良県" },
  { value: "和歌山県", label: "和歌山県" },
  { value: "鳥取県", label: "鳥取県" },
  { value: "島根県", label: "島根県" },
  { value: "岡山県", label: "岡山県" },
  { value: "広島県", label: "広島県" },
  { value: "山口県", label: "山口県" },
  { value: "徳島県", label: "徳島県" },
  { value: "香川県", label: "香川県" },
  { value: "愛媛県", label: "愛媛県" },
  { value: "高知県", label: "高知県" },
  { value: "福岡県", label: "福岡県" },
  { value: "佐賀県", label: "佐賀県" },
  { value: "長崎県", label: "長崎県" },
  { value: "熊本県", label: "熊本県" },
  { value: "大分県", label: "大分県" },
  { value: "宮崎県", label: "宮崎県" },
  { value: "鹿児島県", label: "鹿児島県" },
  { value: "沖縄県", label: "沖縄県" },
];

// 自定义输入组件：稳定常量，首位不是 0 自动补 0（forwardRef 保持库内 DOM 操作正常）
const InputWithAutoZero = React.forwardRef((props, ref) => {
  const { onChange, onInput, ...rest } = props;
  function normalizeEvent(e) {
    // 兜底：只有在回调是函数时才调用，避免 TypeError: onChange is not a function
    const callOnInput = typeof onInput === "function";
    const callOnChange = typeof onChange === "function";

    if (!e || !e.target) {
      if (callOnInput) onInput(e);
      if (callOnChange) onChange(e);
      return;
    }
    const digits = (e.target.value || "").replace(/\D/g, "");
    if (digits && !digits.startsWith("0")) e.target.value = `0${digits}`;
    if (callOnInput) onInput(e);
    if (callOnChange) onChange(e);
  }
  return (
    <input
      ref={ref}
      {...rest}
      onChange={normalizeEvent}
      onInput={normalizeEvent}
      placeholder="090 1234 5678"
    />
  );
});

const CheckoutMsg = () => {
  const location = useLocation();
  const orderInfo = location.state?.orderInfo || null;
  const campaignDetails = location.state?.campaignDetails || null;
  const passedSelectedItems = location.state?.selectedItems || null;
  const passedTotalAmount = location.state?.totalAmount || 0;
  const passedTotalQuantity = location.state?.totalQuantity || 0;

  // 从 navigation state 获取 selectedItems，否则从 location state 获取
  const selectedItems = useMemo(() => {
    return passedSelectedItems || location.state?.selectedItems || [];
  }, [passedSelectedItems, location.state?.selectedItems]);

  // 计算总金额（支持多选）
  const totalAmount = useMemo(() => {
    return selectedItems.reduce(
      (total, item) => total + Number(item.price || 0) * (item.quantity || 1),
      0
    );
  }, [selectedItems]);

  // 计算总数量（支持多选）
  const totalQuantity = useMemo(() => {
    return selectedItems.reduce(
      (total, item) => total + (Number(item.quantity) || 1),
      0
    );
  }, [selectedItems]);

  // 如果有传递过来的总金额和总数量，则使用传递的值，否则使用计算的值
  const displayTotalAmount = passedTotalAmount || totalAmount;
  const SYSTEM_FEE = 220;
  const finalTotalAmount = displayTotalAmount + SYSTEM_FEE;
  const displayTotalQuantity = passedTotalQuantity || totalQuantity || 1;

  // 给图片加上前缀
  const resolveUrl = (raw) => {
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
    return `${LIST_URL.replace(/\/$/, "")}/${String(raw).replace(/^\//, "")}`;
  };
  const pkMessageSentRef = useRef(false);
  const iframeContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [isOrderSummaryExpanded, setIsOrderSummaryExpanded] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [showIframe, setShowIframe] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(720);
  const ad = JSON.parse(localStorage.getItem("ad")) || "donation";
  const [pixelRes, setPixelRes] = useState(null);

  // 在组件加载时就请求 pixel 配置
  useEffect(() => {
    const fetchPixelConfig = async () => {
      try {
        const url = BASE_URL;
        const strippedUrl = url.replace(/^https?:\/\//, "");
        const res = await GetPixelAPI.getpixel(strippedUrl);
        if (res?.data?.data) {
          setPixelRes(res.data.data);
        }
      } catch (error) {
        console.error("Failed to load pixel configuration:", error);
      }
    };

    fetchPixelConfig();
  }, []);

  // 随机生成六位字符（包含数字和字母）
  const generateRandomCode = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  };

  // 获取或生成 userid
  let userid = localStorage.getItem("userid");
  if (!userid) {
    userid = generateRandomCode();
    localStorage.setItem("userid", userid);
  }

  //event_id，要去重，一天之内用一个，存cookie
  const getEventId = (eventType, auxiliaryProps) => {
    // 根据事件类型使用不同的cookie名
    let cookieName = `fb_event_id_${eventType}`;

    if (eventType === "CustomizeProduct") {
      if (auxiliaryProps) {
        cookieName = `fb_event_id_${eventType}_${auxiliaryProps}`;
      }
    }

    let storedEventId = Cookies.get(cookieName);
    if (storedEventId) {
      return storedEventId;
    } else {
      // 使用 10 位随机数字作为 event_id
      const newEventId = Math.round(Math.random() * 10000000000).toString();
      Cookies.set(cookieName, newEventId, { expires: 1 }); // 存储1天
      return newEventId;
    }
  };

  const [paymentInfo, setPaymentInfo] = useState(() => {
    // 优先从 orderDetail 读取之前保存的订单信息
    const savedOrderDetail = localStorage.getItem("orderDetail");
    if (savedOrderDetail) {
      try {
        const orderDetail = JSON.parse(savedOrderDetail);
        // 从 orderDetail 中提取用户信息
        return {
          email: orderDetail.email || "",
          firstName: orderDetail.firstName || "",
          lastName: orderDetail.lastName || "",
          zipCode: orderDetail.zipCode || "",
          state: orderDetail.state || "",
          city: orderDetail.city || "",
          address: orderDetail.address || "",
          phone: orderDetail.phone || "",
          country: orderDetail.country || "JPY",
          selected: orderDetail.country || "JPY",
        };
      } catch (error) {
        console.error("读取订单信息失败:", error);
      }
    }

    // 其次从 userPaymentInfo 读取之前保存的用户信息
    const savedUserInfo = localStorage.getItem("userPaymentInfo");
    if (savedUserInfo) {
      try {
        return JSON.parse(savedUserInfo);
      } catch (error) {
        console.error("读取用户信息失败:", error);
      }
    }

    // 如果没有保存的信息，返回默认值
    return {
      email: "",
      firstName: "",
      lastName: "",
      zipCode: "",
      state: "",
      city: "",
      address: "",
      phone: "",
      country: "JP",
      selected: "JP",
    };
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({}); // 追踪哪些字段已经被触碰过
  const [formIsValid, setFormIsValid] = useState(false);
  const [inputPhone, setInputPhone] = useState("");
  const messageSentRef = useRef(false);

  // 从订单信息构建 currentPlan
  useEffect(() => {
    if (orderInfo) {
      const plan = {
        id: orderInfo.id,
        name: orderInfo.name,
        totalPrice: orderInfo.totalPrice,
        pricePerUnit: orderInfo.basePrice,
        amount: orderInfo.quantity,
        discount: "0%",
        variant: orderInfo.variant,
        insurance: orderInfo.insurance,
        insurancePrice: orderInfo.insurancePrice,
        image: orderInfo.image,
      };
      setCurrentPlan(plan);
    }
  }, [orderInfo]);

  useEffect(() => {
    if (!messageSentRef.current) {
      if (currentPlan) {
        sendMessage(
          `${BASE_URL} [Payment-Page Page]:id=${currentPlan.id}:ad=${ad}:userid=${userid}`
        );
        messageSentRef.current = true;
      } else if (campaignDetails?.id) {
        // Fallback to campaignDetails if currentPlan is null (e.g. from checkout)
        sendMessage(
          `${BASE_URL} [Payment-Page Page]:id=${campaignDetails.id}:ad=${ad}:userid=${userid}`
        );
        messageSentRef.current = true;
      }
    }
  }, [currentPlan, campaignDetails, ad, userid]);

  useEffect(() => {
    const { email, firstName, lastName, zipCode, state, city, address, phone } =
      paymentInfo;

    const fieldsAreFilled =
      email &&
      firstName &&
      lastName &&
      zipCode &&
      state &&
      city &&
      address &&
      phone;

    const noErrors = Object.keys(errors).length === 0;
    const isValid = fieldsAreFilled && noErrors;

    setFormIsValid(isValid);
  }, [paymentInfo, errors]);

  // 初始化时检查是否有保存的邮编，如果有则显示地址字段
  useEffect(() => {
    if (paymentInfo.zipCode) {
      setShowAddressFields(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当 iframe 显示时，自动滚动到 iframe 位置
  useEffect(() => {
    if (showIframe && iframeContainerRef.current) {
      setTimeout(() => {
        iframeContainerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 0);
    }
  }, [showIframe]);

  const validateEmail = (email) => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
    if (!emailRegex.test(email)) {
      return "有効なメールアドレスを入力してください";
    }
    return "";
  };

  //验证手机号码
  const validatePhone = (phoneValue) => {
    if (!phoneValue) return false;

    // 如果是国内号码（以0开头），转换为E.164格式（日本+81）
    let e164Value = phoneValue;
    if (phoneValue && typeof phoneValue === "string") {
      const digits = phoneValue.replace(/\D/g, "");
      if (digits.startsWith("0")) {
        // 去掉前导0，添加+81前缀
        e164Value = "+81" + digits.substring(1);
      } else if (!digits.startsWith("+")) {
        // 如果不是以+开头，也不是以0开头，假设已经去掉了0
        e164Value = "+81" + digits;
      }
    }

    // 使用 isValidPhoneNumber 进行校验
    return isValidPhoneNumber(e164Value);
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;

    // 当用户输入任何内容时（包括空格），清除该字段的错误状态
    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }

    setPaymentInfo({
      ...paymentInfo,
      [name]: value,
    });
  };

  // 统一的 blur 校验处理函数
  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    // 标记该字段已被触碰
    setTouched((prev) => ({ ...prev, [name]: true }));

    switch (name) {
      case "email":
        if (!value.trim()) {
          newErrors.email = "メールアドレスを入力してください！";
        } else {
          const emailError = validateEmail(value.trim());
          if (emailError) {
            newErrors.email = emailError;
          } else {
            delete newErrors.email;
          }
        }
        break;

      case "firstName":
        if (!value.trim()) {
          newErrors.firstName = "名前を入力してください！";
        } else {
          delete newErrors.firstName;
        }
        break;

      case "lastName":
        if (!value.trim()) {
          newErrors.lastName = "苗字を入力してください！";
        } else {
          delete newErrors.lastName;
        }
        break;

      case "zipCode":
        if (!value) {
          newErrors.zipCode = "郵便番号を入力してください！";
        } else {
          const zipNumbers = value.replace(/-/g, "");
          if (zipNumbers.length !== 7) {
            newErrors.zipCode =
              "正しい郵便番号を入力してください（例：123-4567）";
          } else {
            delete newErrors.zipCode;
          }
        }
        break;

      case "state":
        // state 字段是必填的
        if (!value) {
          newErrors.state = "都道府県を選択してください！";
        } else {
          delete newErrors.state;
        }
        break;

      case "city":
        if (!value.trim()) {
          newErrors.city = "市区町村を入力してください！";
        } else {
          delete newErrors.city;
        }
        break;

      case "address":
        if (!value.trim()) {
          newErrors.address = "番地/建物名を入力してください！";
        } else {
          delete newErrors.address;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  const handlePayNow = async () => {
    // 优先使用 selectedItems（支持多选），否则使用 currentPlan
    const planFromSelected =
      selectedItems.length > 0
        ? {
            // 构建多选商品信息
            id: selectedItems.map((item) => item.id || 0).join(","),
            name:
              selectedItems.length === 1
                ? selectedItems[0].name || campaignDetails?.title || ""
                : `${campaignDetails?.title || "複数商品"} (${
                    selectedItems.length
                  } 点)`,
            totalPrice: finalTotalAmount || 0,
            pricePerUnit:
              selectedItems.length === 1 ? selectedItems[0].price || 0 : 0,
            amount: displayTotalQuantity || 1,
            variant: selectedItems
              .map((item) => item.variant || "")
              .filter(Boolean)
              .join(", "),
            insurance: selectedItems
              .map((item) => item.insurance || "")
              .filter(Boolean)
              .join(", "),
            insurancePrice: selectedItems.reduce(
              (sum, item) => sum + (item.insurancePrice || 0),
              0
            ),
            image:
              selectedItems[0]?.img || campaignDetails?.image?.[0]?.url || "",
            // 保留完整的多选信息用于 goods 构建
            selectedItems: selectedItems,
          }
        : null;

    const planToUse = planFromSelected || currentPlan;

    if (
      !planToUse ||
      planToUse.totalPrice === null ||
      planToUse.totalPrice === 0
    ) {
      alert("金額が0円です。注文する商品がありません。");
      return;
    }

    const { email, firstName, lastName, address, city, zipCode, state, phone } =
      paymentInfo;

    // 完整的字段校验
    const validationErrors = {};

    // 邮箱校验
    if (!email) {
      validationErrors.email = "メールアドレスを入力してください！";
    } else {
      const emailError = validateEmail(email.trim());
      if (emailError) {
        validationErrors.email = emailError;
      }
    }

    // 电话号码校验
    if (!phone) {
      validationErrors.phone = "電話番号を入力してください！";
    } else {
      const isPhoneValid = validatePhone(phone);
      if (!isPhoneValid) {
        validationErrors.phone = "無効な電話番号";
      }
    }

    // 姓名校验
    if (!firstName) {
      validationErrors.firstName = "名前を入力してください！";
    } else if (!firstName.trim()) {
      validationErrors.firstName = "名前を入力してください！";
    }

    if (!lastName) {
      validationErrors.lastName = "苗字を入力してください！";
    } else if (!lastName.trim()) {
      validationErrors.lastName = "苗字を入力してください！";
    }

    // 邮编校验
    if (!zipCode) {
      validationErrors.zipCode = "郵便番号を入力してください！";
    } else {
      const zipNumbers = zipCode.replace(/-/g, "");
      if (zipNumbers.length !== 7) {
        validationErrors.zipCode =
          "正しい郵便番号を入力してください（例：123-4567）";
      }
    }

    // 都道府县校验
    if (!state) {
      validationErrors.state = "都道府県を選択してください！";
    }

    // 市区町村校验
    if (!city) {
      validationErrors.city = "市区町村を入力してください！";
    } else if (!city.trim()) {
      validationErrors.city = "市区町村を入力してください！";
    }

    // 番地/建物名校验
    if (!address) {
      validationErrors.address = "番地/建物名を入力してください！";
    } else if (!address.trim()) {
      validationErrors.address = "番地/建物名を入力してください！";
    }

    if (Object.keys(validationErrors).length > 0) {
      // 标记有错误的字段为已触碰，这样才会显示红色边框
      const newTouched = {};
      Object.keys(validationErrors).forEach((key) => {
        newTouched[key] = true;
      });
      setTouched((prev) => ({ ...prev, ...newTouched }));
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);

      const url = BASE_URL;
      const strippedUrl = url.replace(/^https?:\/\//, "");

      // 如果 pixelRes 还没有加载，尝试再次获取
      let currentPixelRes = pixelRes;
      if (!currentPixelRes) {
        try {
          const res = await GetPixelAPI.getpixel(strippedUrl);
          if (res?.data?.data) {
            currentPixelRes = res.data.data;
            setPixelRes(currentPixelRes);
          }
        } catch (e) {
          console.error("Failed to fetch pixel config on submit", e);
        }
      }

      // 检查 pixelRes 是否已加载
      if (!currentPixelRes) {
        alert(
          "お支払い設定を読み込んでいます。少々お待ちしてからもう一度お試しください。"
        );
        setIsLoading(false);
        return;
      }

      // 处理电话号码：由于 onChange 已经格式化为日本国内格式（以0开头），直接使用
      let phoneNumberOnly = phone;

      // 确保电话号码格式正确（去掉空格和横线）
      if (phoneNumberOnly) {
        phoneNumberOnly = phoneNumberOnly.replace(/[-\s]/g, "");

        // 如果不是以0开头，尝试补0（防御性处理）
        if (!phoneNumberOnly.startsWith("0") && phoneNumberOnly.length >= 10) {
          phoneNumberOnly = "0" + phoneNumberOnly;
        }
      }

      // 构建 goods 数据（支持多选）
      const goods =
        selectedItems.length > 0
          ? selectedItems.map((item) => ({
              goods_title: item.name || campaignDetails?.title || "", // 商品名称
              goods_sku: item.variant || "", // 商品sku
              goods_price: (Number(item.price) || 0) * (item.quantity || 1), // 商品总价
              goods_image: item.img || "", // 商品图片
              goods_num: item.quantity || 1, // 商品数量
            }))
          : [
              {
                goods_title: planToUse.name, // 商品名称
                goods_sku: planToUse.variant, // 商品sku
                goods_price: Number(planToUse.totalPrice) || 0, // 商品原价
                goods_image: planToUse.image, // 商品图片
                goods_num: planToUse.amount || 1, // 商品数量
              },
            ];

      const goodsJson = JSON.stringify(goods);

      console.log("pixelRes-----", pixelRes);

      let orderDetail = {
        ad: JSON.parse(localStorage.getItem("ad")) || "defaultAd",
        success_url: `${
          window.location.origin
        }/paysuccess?email=${encodeURIComponent(
          email.trim()
        )}&phone=${encodeURIComponent(phone.trim())}&detailId=${
          campaignDetails?.id
        }`,
        // success_url: "https://localhost:3000/paysuccess",
        cancel_url: `${window.location.origin}/payfial`,
        // cancel_url: "https://localhost:3000/payfial",
        back_url: `${BASE_URL}/checkout`, // 返回主页的URL
        locale: campaignDetails.locale,
        vehicleModel: campaignDetails?.title, // 产品型号
        host: window.location.host, // 当前域名
        headerLogo: "/head/ico-jp.png", // 头部Logo
        faviconLogo: "/head/main-jp.png", // 网站图标
        successLogo: resolveUrl(campaignDetails?.image[0].url) || "", // 成功页面Logo
        successText: "ご注文ありがとうございます！", // 成功提示文本
        indexTitle: campaignDetails.title,
        indexDesc: campaignDetails.title,
        brandColor: donateDeepColor || "#e73650", // 品牌颜色
        otherInfo: currentPixelRes?.groupid || "", // 其他信息
        img: "", //商品图片
        title: campaignDetails
          ? generateDonationUrl(campaignDetails)
          : `/donationDetails/${campaignDetails?.id}`, // 捐款名称
        //TODO:
        goods: goodsJson, //传入转json格式的对象
        payment_intent_id: "",
        type: 10,
        curcrency: campaignDetails.currency_name,
        email: email.trim(),
        phone: phoneNumberOnly,
        state: state, // 都道府県
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        snCode: "",
        city: city, // 市区町村
        zipCode: zipCode,
        address: address, // 番地/建物名（line1）
        detailAddress: "", // detailAddress 传入空字符串（不再使用 address2）
        country: paymentInfo.country,
        id: "",
        orderNu: "",
        tgid: currentPixelRes?.tgid || "",
        fbqToken: campaignDetails.fbq_token || "",
        pixel: campaignDetails.pixel || "",
        amount:
          Math.round(parseFloat(planToUse.totalPrice)) ||
          currentPixelRes?.amount ||
          0,
        amount_fast:
          Math.round(parseFloat(planToUse.totalPrice)) ||
          currentPixelRes?.amount ||
          0,
        // TODO:
        stusername: currentPixelRes?.stusername || localStorage.getStUsername(),
        fakeEmail: campaignDetails.email_id || "",
        donationID: campaignDetails?.id,
      };

      // 构建产品事件数据（支持多选）
      const contentIds = ["donation"];

      const numItems =
        selectedItems.length > 0
          ? selectedItems.reduce((sum, item) => sum + (item.quantity || 1), 0)
          : planToUse.amount;

      const contents =
        selectedItems.length > 0
          ? selectedItems.map((item) => ({
              id: (item.id || 0).toString(),
              quantity: item.quantity || 1,
              title: item.name || campaignDetails?.title || "",
            }))
          : [
              {
                id: planToUse.id.toString(),
                quantity: planToUse.amount,
                title: planToUse.name,
              },
            ];

      let userdata = {
        em: orderDetail.email,
        ph: orderDetail.phone,
        fn: orderDetail.firstName,
        ln: orderDetail.lastName,
        ct: orderDetail.city,
        st: orderDetail.state,
        zp: orderDetail.zipCode,
        country: orderDetail.country,
        external_id: "",
        client_ip_address: "",
        client_user_agent: "",
        fbc: "",
        fbp: "",
        lead_id: "",
        anon_id: "",
        madid: "",
        page_id: "",
        page_scoped_user_id: "",
        ctwa_clid: "",
        ig_account_id: "",
        ig_sid: "",
      };

      const eventData = {
        content_ids: contentIds,
        content_name: planToUse.name, // 商品名称列表
        value: Math.round(parseFloat(planToUse.totalPrice)), // 总金额
        currency: "jpy", // 货币单位（根据你的设置调整）
        num_items: numItems,
        contents: contents,
      };

      //getUrl
      const response = await getUrl(orderDetail);

      // 再尝试发送完整数据（可能因为数据过大被截断）
      sendMessage("getUrl返回------- " + JSON.stringify(response.data));

      if (!pkMessageSentRef.current) {
        sendMessage(
          `${BASE_URL} [${planToUse.totalPrice} get url]:id=${campaignDetails?.id}:ad=${ad}:userid=${userid}`
        );
        // sendMessage(JSON.stringify(response.data.data));
        pkMessageSentRef.current = true;
      }
      const event_id = getEventId("InitiateCheckout");

      if (response.data.code === 1 && response.data.data) {
        // 在 getUrl 成功后调用 Pixel 和 Facebook API
        PixelWithEvent("InitiateCheckout", event_id);

        // 同时触发 Facebook Pixel 事件
        FacebookAPI.Facebook(
          orderDetail.fbqToken,
          orderDetail.pixel,
          event_id,
          "AddPaymentInfo",
          eventData,
          userdata
        );

        sendMessage(
          BASE_URL +
            `[${planToUse.totalPrice} AddPaymentInfo]:id=${campaignDetails?.id}:ad=${ad}:userid=${userid}  `
        );
        let data = response.data.data;
        const orderNo = data.orderNo;
        if (orderNo && orderNo.trim() !== "") {
          localStorage.setItem("orderNo", orderNo);
          console.log(data);
          sendMessage(
            `准备跳转------- ${data.redirectlink}#/?orderNo=${orderNo}&service=st`
          );
          if (data.paymentType === 1) {
            window.location.href = data.redirectlink;
            return;
          }
          const redirectUrl = `${data.redirectlink}#/?orderNo=${orderNo}&service=st&isEmbedded=true`;
          // iframe 嵌入：仅设置地址并展示，不启用全局 loading（内嵌页自带 loading）
          const initialHeight =
            typeof window !== "undefined" && window.innerHeight
              ? window.innerHeight
              : 720;
          setIframeUrl(redirectUrl);
          setShowIframe(true);
          setIframeHeight(initialHeight);
          // window.location.href = `${data.redirectlink}#/?orderNo=${orderNo}&service=st`;
        }
        // 保存用户填写的信息到 localStorage，供下次使用
        // localStorage.setItem("userPaymentInfo", JSON.stringify(paymentInfo));
        // const st_id = response.data.data.st_id;
        // setPublishableKey(response.data.data.api_publishable_key);
        // setShowPaymentForm(true);

        // setSuccessUrl(`${window.location.origin}/paysuccess`);

        // 设置产品信息并持久化到本地，成功頁面可讀取
        const productInfo = {
          id: planToUse.id,
          name: planToUse.name,
          price: Math.round(parseFloat(planToUse.totalPrice)),
          variant: planToUse.variant,
          insurance: planToUse.insurance,
          quantity: planToUse.amount,
          image: planToUse.image,
        };
        localStorage.setItem("product", JSON.stringify(productInfo));
      }
      // 保存訂單資訊到本地，成功頁與後續頁面可使用
      localStorage.setItem("orderDetail", JSON.stringify(orderDetail));
    } catch (error) {
      console.error("お支払いエラー:", error);
      sendMessage("getUrl报错------- " + JSON.stringify(error));
      sendMessage(
        `Error details: ${JSON.stringify({
          message: error.message,
          stack: error.stack,
          response: error.response
            ? {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
              }
            : null,
          request: error.request ? "Request sent but no response" : null,
        })}`
      );
      alert(
        "お支払い処理中にエラーが発生しました。もう一度お試しください。\nページを再読み込みしてください。"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleZipCodeChange = (e) => {
    const { value } = e.target;
    const numbersOnly = value.replace(/[^\d]/g, "");

    if (numbersOnly.length > 7) return;

    let formattedZip = numbersOnly;
    if (numbersOnly.length > 3) {
      formattedZip = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3)}`;
    }

    setPaymentInfo((prev) => ({
      ...prev,
      zipCode: formattedZip,
    }));

    // 清除错误状态
    if (errors.zipCode) {
      const newErrors = { ...errors };
      delete newErrors.zipCode;
      setErrors(newErrors);
    }

    if (numbersOnly.length > 0) {
      setShowAddressFields(true);
    } else {
      setShowAddressFields(false);
    }
  };

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleOrderSummary = () => {
    setIsOrderSummaryExpanded(!isOrderSummaryExpanded);
  };

  return (
    <>
      <header className="checkout-header">
        <div className="checkout-header__nav">
          <div className="checkout-header__nav_inner">
            <Link to="/" className="checkout-header__logo" onClick={toTop}>
              <img
                alt=""
                src={process.env.PUBLIC_URL + "/head/main-jp.png"}
                style={{ height: "24px" }}
              />
            </Link>
          </div>
        </div>
      </header>
      <div className="product-wrapper">
        <div className="checkout-steps">
          <div className="step ">
            <div className="step-circle">1</div>
            <div className="step-label">リターン選択</div>
          </div>
          <div className="step-line"></div>
          <div className="step active">
            <div className="step-circle">2</div>
            <div className="step-label">入力</div>
          </div>
          <div className="step-line"></div>
          <div className="step">
            <div className="step-circle">3</div>
            <div className="step-label">支援完了</div>
          </div>
        </div>
        <div className="product_box">
          <div className="product_box_right">
            <div className="payment-modal-overlay">
              <div className="payment-modal">
                <div className="payment-modal-content">
                  <div className="payment-form">
                    <div className="self_div">
                      {/* 手机端订单摘要 */}
                      <div className="mobile-order-summary">
                        <div
                          className="order-summary-header"
                          onClick={toggleOrderSummary}
                        >
                          <div className="summary-toggle-text">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="cart-icon"
                            >
                              <path
                                d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L5.12 14.88C4.66 15.34 5 16.12 5.66 16.12H19M7 13H5.12"
                                stroke="#197bbd"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <circle
                                cx="9"
                                cy="21"
                                r="1"
                                stroke="#197bbd"
                                strokeWidth="2"
                              />
                              <circle
                                cx="20"
                                cy="21"
                                r="1"
                                stroke="#197bbd"
                                strokeWidth="2"
                              />
                            </svg>
                            <span style={{ color: "#197bbd" }}>
                              {isOrderSummaryExpanded
                                ? "注文内容を非表示"
                                : "注文内容の表示"}
                            </span>
                            <span
                              className={`arrow-icon ${
                                isOrderSummaryExpanded ? "up" : "down"
                              }`}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#197bbd"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </span>
                          </div>
                          <div className="summary-total-price">
                            {finalTotalAmount.toLocaleString("en", {
                              maximumFractionDigits: 0,
                            })}円
                          </div>
                        </div>

                        {isOrderSummaryExpanded && (
                          <div className="order-summary-content">
                            {/* 支持多选商品显示 */}
                            {(selectedItems.length > 0
                              ? selectedItems
                              : currentPlan
                              ? [currentPlan]
                              : []
                            ).map((item, index) => (
                              <React.Fragment key={item.id || index}>
                                <div className="cart-items-section">
                                  <div className="cart-item-card">
                                    <div className="cart-item-image">
                                      <img
                                        src={
                                          item.image ||
                                          resolveUrl(
                                            campaignDetails?.image?.[0]?.url
                                          )
                                        }
                                        alt={item.name}
                                      />
                                    </div>
                                    <div className="cart-item-info">
                                      <h4 className="cart-item-name">
                                        {item.name ||
                                          campaignDetails?.title ||
                                          ""}
                                      </h4>
                                      <p className="cart-item-variant">
                                        {item.variant || ""}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="summary-divider"></div>
                              </React.Fragment>
                            ))}

                            {/* 总计部分 */}
                            <div className="summary-item">
                              <div className="summary-label">小計</div>
                              <div className="summary-value">
                                {displayTotalAmount.toLocaleString("en", {
                                  maximumFractionDigits: 0,
                                })}円 「税込」
                              </div>
                            </div>

                            <div className="summary-item">
                              <div className="summary-label">
                                システム利用料
                              </div>
                              <div className="summary-value">
                                {SYSTEM_FEE.toLocaleString("en", {
                                  maximumFractionDigits: 0,
                                })}円
                              </div>
                            </div>

                            <div className="summary-total">
                              <div className="summary-label">合計:</div>
                              <div className="summary-value">
                                {finalTotalAmount.toLocaleString("en", {
                                  maximumFractionDigits: 0,
                                })}円 「税込」
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <h2>連絡先</h2>

                      <div className="name-inputs-row">
                        <div className="name-input-wrapper">
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            placeholder="苗字 例）東京:"
                            value={paymentInfo.lastName}
                            onChange={handlePaymentChange}
                            onBlur={handleInputBlur}
                            className={
                              touched.lastName && errors.lastName ? "error" : ""
                            }
                          />
                          {touched.lastName && errors.lastName && (
                            <p className="error-message">{errors.lastName}</p>
                          )}
                        </div>
                        <div className="name-input-wrapper">
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            placeholder="名前 例）太郎:"
                            value={paymentInfo.firstName}
                            onChange={handlePaymentChange}
                            onBlur={handleInputBlur}
                            className={
                              touched.firstName && errors.firstName
                                ? "error"
                                : ""
                            }
                          />
                          {touched.firstName && errors.firstName && (
                            <p className="error-message">{errors.firstName}</p>
                          )}
                        </div>
                      </div>

                      <input
                        type="text"
                        id="email"
                        name="email"
                        placeholder="メールアドレス:"
                        value={paymentInfo.email}
                        onChange={handlePaymentChange}
                        onBlur={handleInputBlur}
                        className={touched.email && errors.email ? "error" : ""}
                      />
                      {touched.email && errors.email && (
                        <p className="error-message">{errors.email}</p>
                      )}

                      <ReactFlagsSelect
                        selected={paymentInfo.selected}
                        onSelect={(code) => {
                          setPaymentInfo((prevData) => ({
                            ...prevData,
                            selected: code,
                            country: code,
                          }));
                        }}
                        disabled={true}
                        className="check-select"
                      />
                      {errors.country && (
                        <p className="error-message">{errors.country}</p>
                      )}

                      <PhoneInput
                        // value={paymentInfo.phone}
                        onBlur={(e) => {
                          // 处理不同类型的输入
                          const phoneValue =
                            typeof e === "string" ? e : e?.target?.value;

                          // 如果是国内号码（以0开头），转换为E.164格式（日本+81）
                          let e164Value = phoneValue;
                          if (phoneValue && typeof phoneValue === "string") {
                            const digits = phoneValue.replace(/\D/g, "");
                            if (digits.startsWith("0")) {
                              // 去掉前导0，添加+81前缀
                              e164Value = "+81" + digits.substring(1);
                            }
                          }

                          // 使用 isValidPhoneNumber 进行校验
                          if (e164Value && isValidPhoneNumber(e164Value)) {
                            setInputPhone("");
                            if (errors.phone) {
                              const newErrors = { ...errors };
                              delete newErrors.phone;
                              setErrors(newErrors);
                            }
                          } else {
                            setInputPhone("無効な電話番号");
                          }
                          // 标记为已触碰
                          setTouched((prev) => ({ ...prev, phone: true }));
                        }}
                        onChange={(value) => {
                          // 当 value 未生成（号码未完整），不覆盖 phone，保持用户的原始输入（包括前缀 0）
                          if (!value) return;

                          // 将 E.164 转为日本国内显示格式，例如 090 1234 5678
                          const domestic = formatPhoneNumber(value) || "";

                          // 保底确保以 0 开头（理论上 JP 会以 0 开头，但做一次防御处理）
                          const withZero =
                            domestic && domestic[0] !== "0"
                              ? `0${domestic}`
                              : domestic;

                          const format = withZero
                            .replaceAll("-", "")
                            .replaceAll(" ", "");

                          setPaymentInfo((prevData) => ({
                            ...prevData,
                            phone: format,
                          }));
                          // 清除电话号码错误状态
                          setInputPhone("");
                          if (errors.phone) {
                            const newErrors = { ...errors };
                            delete newErrors.phone;
                            setErrors(newErrors);
                          }
                        }}
                        defaultCountry="JP"
                        countries={["JP"]}
                        addInternationalOption={false}
                        international={false}
                        limitMaxLength={true}
                        countrySelectProps={{ disabled: true }}
                        displayInitialValueAsLocalNumber
                        showCountryCallingCode={false}
                        className="check-input"
                        inputStyle={{ width: "100%" }}
                        inputComponent={InputWithAutoZero}
                        style={{
                          borderColor: errors.phone ? "#b91c1c" : "#d1d5db",
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderRadius: 4,
                          padding: "0 11px",
                        }}
                      />
                      {inputPhone && (
                        <p className="error-message">{inputPhone}</p>
                      )}

                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        placeholder="郵便番号 (例: 123-4567):"
                        value={paymentInfo.zipCode}
                        onChange={handleZipCodeChange}
                        onBlur={handleInputBlur}
                        className={
                          touched.zipCode && errors.zipCode ? "error" : ""
                        }
                        maxLength="8"
                      />
                      {touched.zipCode && errors.zipCode && (
                        <p className="error-message">{errors.zipCode}</p>
                      )}
                      <p className="zipcode-hint">
                        郵便番号がわからない方は
                        <a
                          href="https://www.post.japanpost.jp/zipcode/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="zipcode-link"
                        >
                          こちらをご覧ください
                          <svg
                            viewBox="0 0 24 24"
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </a>
                      </p>

                      {showAddressFields && (
                        <>
                          <select
                            id="state"
                            name="state"
                            value={paymentInfo.state}
                            onChange={handlePaymentChange}
                            onBlur={handleInputBlur}
                            className={
                              touched.state && errors.state ? "error" : ""
                            }
                          >
                            <option value="">都道府県を選択してください</option>
                            {JAPAN_PREFECTURES.map((prefecture) => (
                              <option
                                key={prefecture.value}
                                value={prefecture.value}
                              >
                                {prefecture.label}
                              </option>
                            ))}
                          </select>
                          {touched.state && errors.state && (
                            <p className="error-message">{errors.state}</p>
                          )}

                          <input
                            type="text"
                            id="city"
                            name="city"
                            placeholder="市区町村:"
                            value={paymentInfo.city}
                            onChange={handlePaymentChange}
                            onBlur={handleInputBlur}
                            className={
                              touched.city && errors.city ? "error" : ""
                            }
                          />
                          {touched.city && errors.city && (
                            <p className="error-message">{errors.city}</p>
                          )}

                          <input
                            type="text"
                            id="address"
                            name="address"
                            placeholder="番地/建物名:"
                            value={paymentInfo.address}
                            onChange={handlePaymentChange}
                            onBlur={handleInputBlur}
                            className={
                              touched.address && errors.address ? "error" : ""
                            }
                          />
                          {touched.address && errors.address && (
                            <p className="error-message">{errors.address}</p>
                          )}
                        </>
                      )}

                      {!showIframe && (
                        <div className="check_all_btn" ref={iframeContainerRef}>
                          <button
                            className={`pay-now-button ${
                              formIsValid ? "valid" : ""
                            }`}
                            onClick={handlePayNow}
                            disabled={!formIsValid || isLoading}
                          >
                            <span className="button-content">
                              {isLoading ? (
                                <svg
                                  className="button-spinner"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                              ) : (
                                <span>今すぐ購入</span>
                              )}
                            </span>
                          </button>

                          <div className="secure-payment">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M8 1L2 4v3c0 4.418 2.682 8 6 9 3.318-1 6-4.582 6-9V4L8 1z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6 8l1.5 1.5L11 6"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            安全な支払い
                          </div>
                        </div>
                      )}
                      {/* iframe 支付页嵌入区：需要时展示，并在加载完成后隐藏 Loading */}
                      {showIframe && iframeUrl ? (
                        <div className="input-row">
                          <div
                            style={{
                              width: "100%",
                              height: iframeHeight,
                              borderRadius: 8,
                              overflow: "hidden",
                            }}
                          >
                            <iframe
                              title="checkout-iframe"
                              src={iframeUrl}
                              style={{
                                width: "100%",
                                minHeight: "100%",
                                border: "none",
                              }}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="payment-summary">
                    <div className="campaign-summary-card">
                      <div className="campaign-image">
                        <img
                          src={resolveUrl(campaignDetails?.image[0].url)}
                          alt=""
                        />
                      </div>
                      <h3 className="campaign-title">
                        {campaignDetails?.title}
                      </h3>
                      <div className="organizer">
                        <img
                          src={
                            resolveUrl(campaignDetails?.avatar) ||
                            "/main-jp/person.jpg"
                          }
                          alt=""
                          className="organizer-avatar"
                        />
                        <span>{campaignDetails?.name}</span>
                      </div>
                      <div className="campaign-amount">
                        <span className="amount-label">支援金額:</span>
                        <span className="amount-value">
                          {finalTotalAmount.toLocaleString("en", {
                            maximumFractionDigits: 0,
                          })}円
                        </span>
                      </div>
                      <div className="campaign-quantity">
                        <span className="quantity-label">数量:</span>
                        <span className="quantity-value">
                          {displayTotalQuantity}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutMsg;
